/**
 * New contact.
 *
 * Built to docs/references/23-new-contact.png, which is the screen this
 * should have been from the start. What that reference settles:
 *
 *   It is a SCREEN, not a panel inside the contact list. Back arrow,
 *   "New contact", and the list is gone while you are on it. My first
 *   attempt was a card wedged under three rows of alternatives, which is
 *   what pushed its own error message off the bottom of the window.
 *
 *   Icons sit in a left gutter, one per group — person, phone — rather
 *   than labels above every field.
 *
 *   Fields are outlined with the placeholder inside, not a label above.
 *
 *   Country is a select with its label floating in a notch in the border,
 *   showing the code (NG +234), and it sits BESIDE the number at roughly
 *   40/60 rather than above it.
 *
 *   Save is a full-width pill pinned to the bottom, disabled until the
 *   form is worth submitting — not a button inline after the last field.
 *
 * TWO ROWS FROM THE REFERENCE ARE DELIBERATELY ABSENT.
 *
 *   Username. ZIvapp has no usernames — no column, no lookup, nothing to
 *   search. The field would accept text and discard it, which is the
 *   same class of thing as the dead buttons reported alongside this.
 *   It goes in the day usernames exist.
 *
 *   "Sync contact to phone". There is no phone address book to sync to
 *   from a browser. It belongs in the native app, where it can do
 *   something.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  contacts as contactService, chats, toE164, isValidE164, userMessage
} from '@zivapp/shared'
import { COUNTRIES_BY_NAME, countryByCode } from '@zivapp/shared/countries'
import { useAuth } from '../context/index.js'
import './NewContact.css'

const DEFAULT_CODE = 'NG'

export default function NewContact () {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [countryCode, setCountryCode] = useState(DEFAULT_CODE)
  const [phone, setPhone] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const country = countryByCode(countryCode)

  /** The typed number as E.164, or null while it is not yet dialable. */
  const e164 = useMemo(() => {
    const raw = phone.trim()
    if (!raw) return null
    try {
      const value = toE164(raw, country?.dial ?? '234')
      return isValidE164(value) ? value : null
    } catch {
      return null
    }
  }, [phone, country])

  const canSave = Boolean(e164) && !busy

  async function save (event) {
    event.preventDefault()
    if (!canSave) return

    // Your own number, before asking the server. findByPhone excludes the
    // caller, so this came back empty and was reported as "nobody is
    // using ZIvapp with <your own number>".
    if (user?.phone && e164 === user.phone) {
      setError('That is your own number.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      const found = await contactService.findByPhone(e164)
      if (!found) {
        setError(
          `Nobody is using ZIvapp with ${e164} yet. ` +
          'They need to sign up before you can message them.'
        )
        return
      }

      const typed = `${firstName.trim()} ${lastName.trim()}`.trim()
      const savedName = typed || found.displayName || e164
      await contactService.add({ userId: found.id, savedName })

      const chat = await chats.getOrCreateDirect(found.id)
      navigate(`/chats/${chat.id}`, { replace: true })
    } catch (cause) {
      setError(userMessage(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="nco" onSubmit={save}>
      <header className="nco__bar">
        <button
          type="button"
          className="nco__back"
          onClick={() => navigate('/contacts')}
          aria-label="Back"
        >
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M20 12H4m0 0 6-6m-6 6 6 6" stroke="currentColor" strokeWidth="1.9"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h1 className="nco__title">New contact</h1>
      </header>

      <div className="nco__body">
        <div className="nco__group">
          <span className="nco__gutter" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="8.2" r="3.7" stroke="currentColor" strokeWidth="1.7" />
              <path d="M4.9 19.6v-1a5 5 0 0 1 5-5h4.2a5 5 0 0 1 5 5v1"
                stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            </svg>
          </span>

          <div className="nco__fields">
            <input
              className="nco__input"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              placeholder="First name"
              maxLength={40}
              data-testid="new-contact-first"
              autoFocus
            />
            <input
              className="nco__input"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              placeholder="Last name"
              maxLength={40}
              data-testid="new-contact-last"
            />
          </div>
        </div>

        <div className="nco__group">
          <span className="nco__gutter" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M6.6 3h3l1.5 4-2.2 1.6a12 12 0 0 0 5.5 5.5L16 11.9l4 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.2 2 2 0 0 1 6.6 3Z"
                stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
            </svg>
          </span>

          <div className="nco__fields nco__fields--row">
            {/* The label floats in a notch in the border, as in the
                reference — the select shows only the code, which is not
                self-describing without it. */}
            <span className="nco__select-wrap">
              <span className="nco__notch">Country</span>
              <select
                className="nco__select"
                value={countryCode}
                onChange={e => setCountryCode(e.target.value)}
                aria-label="Country"
                data-testid="new-contact-country"
              >
                {COUNTRIES_BY_NAME.map(c => (
                  <option key={c.code} value={c.code}>{c.code} +{c.dial}</option>
                ))}
              </select>
              <svg className="nco__chevron" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>

            <input
              className="nco__input nco__input--phone"
              value={phone}
              onChange={e => { setPhone(e.target.value); setError(null) }}
              placeholder="Phone"
              inputMode="tel"
              data-testid="new-contact-phone"
            />
          </div>
        </div>

        {e164 && (
          <p className="nco__hint">Will be saved as {e164}</p>
        )}

        {error && (
          <p
            className="nco__error"
            role="alert"
            /* Scrolled to as well as placed in the flow: an error you
               cannot see is the bug this screen was rebuilt to fix. */
            ref={el => el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })}
          >
            {error}
          </p>
        )}
      </div>

      {/* Pinned to the bottom, full width, disabled until there is a
          number worth looking up — as in the reference. */}
      <footer className="nco__foot">
        <button
          type="submit"
          className="nco__save"
          disabled={!canSave}
          data-testid="new-contact-save"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
      </footer>
    </form>
  )
}
